import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";
import { assigneeOf } from "../lib/agents";
import { useIsDesktop } from "../lib/media";
import { queryKeys } from "../lib/queryKeys";
import { useTicketRoom } from "../lib/socket";
import {
  type Message,
  type PresenceState,
  type TicketActivity,
} from "../types";
import { ActivityLink } from "./ActivityDrawer";
import { AssigneeSheet } from "./AssigneeSheet";
import { Avatar } from "./ui/Avatar";
import { cn } from "./ui/cn";
import {
  describeParticipant,
  useThreadParticipants,
  type Participant,
} from "../lib/participants";
import { ActivityEntry, isThreadActivity } from "./ActivityEntry";
import { TicketSubject } from "./TicketSubject";
import { Composer } from "./Composer";
import { MessageBubble, messageAnchor } from "./MessageBubble";
import { WatchButton } from "./WatchButton";
import { PresenceBar } from "./PresenceBar";
import type { ComposerIntent, IntentKind } from "../lib/quoting";
import { absoluteTime } from "../lib/time";
import { foldedCount } from "../lib/thread-fold";

/**
 * Messages shown before the thread starts collapsing -- emails and replies,
 * that is, not notes, which never count towards it (see `foldedCount`).
 *
 * Three, from the brief. It is the point at which a thread stops being a short
 * exchange you read whole and starts being a history you scroll -- and the last
 * three carry the question, the answer and the reply to it.
 */
const VISIBLE_MESSAGES = 3;

/**
 * The same, on a phone, where it can afford to be more.
 *
 * There every message but the newest is already folded to a one-line row, so
 * showing eight costs about what three open ones did on a desktop -- and seeing
 * the run of who said what before tapping into one is most of what the folded
 * rows are for.
 */
const PHONE_VISIBLE_MESSAGES = 8;

/**
 * One row of the thread: something said, or something done.
 *
 * `at` is precomputed rather than parsed in the comparator, because the two
 * carry their time under different names (`sentAt`, `createdAt`) and sorting
 * over a union that has to re-derive that on every comparison is both slower
 * and easy to get subtly wrong.
 */
type ThreadItem =
  | { kind: "message"; at: number; message: Message }
  | { kind: "activity"; at: number; entry: TicketActivity };

export function TicketDetail({
  ticketId,
  currentUserId,
  sla,
}: {
  ticketId: number;
  currentUserId: number;
  /**
   * The SLA's time left, drawn by the page that holds the queue -- it has the
   * pipelines and the business calendar the countdown is worked out from.
   * Shown on the phone's action bar; absent when there is nothing to show.
   */
  sla?: React.ReactNode;
}) {
  const queryClient = useQueryClient();

  /*
   * Presence flips to "replying" the moment the composer has content. That
   * signal is the whole point of the realtime layer for a shared inbox: it is
   * what stops two agents independently writing the same answer.
   */
  const [presenceState, setPresenceState] = useState<PresenceState>("viewing");
  const presence = useTicketRoom(ticketId, presenceState);

  /*
   * Reset by the key on this component in TicketsView, which remounts it per
   * ticket -- so expanding one long thread does not expand the next one.
   */
  const [showAll, setShowAll] = useState(false);

  const detail = useQuery({
    queryKey: queryKeys.ticket(ticketId),
    queryFn: () => api.ticket(ticketId),
  });

  /*
   * The phone layout, which differs in behaviour and not only in styling: the
   * reply box starts folded behind a bar, earlier messages fold to a line, and
   * the ticket's heading scrolls away with the thread. See the notes at each.
   */
  const isDesktop = useIsDesktop();

  /*
   * Whether the reply box is open on a phone.
   *
   * Folded by default because on a 390x700 screen the composer -- mode
   * switch, To line, toolbar, editor, Send -- took nearly half the height and
   * left the conversation a letterbox to be read through. Gmail's app keeps
   * the thread whole and puts Reply at the bottom of it; this does the same.
   *
   * Folded with `hidden`, never unmounted, so a half-written reply, its
   * uploads and the "replying" presence all survive folding it to read
   * something further up.
   */
  const [composerOpen, setComposerOpen] = useState(false);

  /* The assignee picker, on a phone. */
  const [assigning, setAssigning] = useState(false);

  /*
   * Messages unfolded by hand on a phone, where everything but the newest
   * starts as a one-line row. Ids rather than indexes, so a message arriving
   * over the socket does not shift which ones are open.
   */
  const [unfolded, setUnfolded] = useState<ReadonlySet<number>>(new Set());
  const toggleFolded = (messageId: number): void =>
    setUnfolded((current) => {
      const next = new Set(current);
      if (!next.delete(messageId)) next.add(messageId);
      return next;
    });

  /* The same list the details panel and the queue's dropdowns use -- one
     cached request, shared. */
  const users = useQuery({
    queryKey: queryKeys.users,
    queryFn: api.users,
    staleTime: 5 * 60_000,
  });

  /*
   * The lists go too, not just this ticket. Sending a reply moves the ticket's
   * status, preview and position in the queue, and the list beside this pane is
   * a separate query that knows none of that. The socket now broadcasts it as
   * well, so this is the belt to that braces -- a dropped connection must not
   * be the difference between a correct screen and a stale one.
   */
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.ticket(ticketId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.ticketsRoot });
    void queryClient.invalidateQueries({ queryKey: queryKeys.viewCounts });
  };

  /*
   * Reassigning and closing from the phone's action bar.
   *
   * The same endpoints the details panel drives, refreshed the same way. The
   * picked name is shown from the moment of the tap (`variables`, below)
   * rather than after the round trip: on a phone connection that is long
   * enough for somebody to tap again, sure the first one missed.
   */
  const assign = useMutation({
    mutationFn: (assigneeId: number | null) =>
      api.setAssignee(ticketId, assigneeId),
    onSettled: invalidate,
  });
  const changeStatus = useMutation({
    mutationFn: (status: "open" | "closed") => api.setStatus(ticketId, status),
    onSettled: invalidate,
  });

  /*
   * What a message's menu asked the composer to do, if anything.
   *
   * Held here rather than in the composer because the request comes from a
   * bubble, and these two are siblings -- the alternative is a bubble reaching
   * sideways into the composer's state, which is how a thread ends up able to
   * clear somebody's draft.
   */
  const [intent, setIntent] = useState<ComposerIntent | null>(null);
  const intents = useRef(0);

  const act = (kind: IntentKind, message: Message): void => {
    intents.current += 1;
    setIntent({ kind, message, nonce: intents.current });
    /* Reply, Reply all and Forward from a message's menu are all asking for
       the composer, so on a phone it has to come out from behind its bar. */
    setComposerOpen(true);
  };

  /**
   * A link to one message, on the clipboard.
   *
   * Built from the address bar rather than composed from the route, so it
   * carries whichever view the agent is reading the ticket in -- a colleague
   * following it lands in a list that makes sense, not in someone else's
   * saved view.
   */
  const copyLink = async (messageId: number): Promise<boolean> => {
    const url = `${window.location.origin}${window.location.pathname}#${messageAnchor(
      messageId,
    )}`;
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch {
      /* Denied in insecure contexts. The menu says so rather than looking
         like it worked. */
      return false;
    }
  };

  /*
   * Arriving on a link to a particular message.
   *
   * Two things have to happen that a bare `#anchor` cannot do on its own: a
   * long thread opens collapsed, so the message being linked to is very often
   * not in the DOM to scroll to; and once it is there, it needs pointing out,
   * because landing halfway down a conversation with nothing marked is
   * indistinguishable from having scrolled there by hand.
   */
  const location = useLocation();
  const [highlight, setHighlight] = useState<number | null>(null);
  /* So a refetch mid-read does not yank the thread back to the linked
     message every time the socket reports something. */
  const scrolledTo = useRef<number | null>(null);

  useEffect(() => {
    const match = /^#message-(\d+)$/.exec(location.hash);
    if (!match) return;
    setHighlight(Number(match[1]));
  }, [location.hash, ticketId]);

  const loadedMessages = detail.data?.messages;

  /*
   * Everybody on the thread, not just the contact it is filed against. The
   * header used to print one address, which is the right answer for a ticket
   * with one reader and misleading for the ones that matter -- an RMA Ccd to a
   * facilities manager and an account manager reads as a private exchange with
   * one person until somebody hits reply all and finds out otherwise.
   */
  const people = useThreadParticipants(
    loadedMessages,
    detail.data?.channel.address,
  );

  useEffect(() => {
    if (highlight === null || !loadedMessages) return;
    if (!loadedMessages.some((message) => message.id === highlight)) return;
    if (scrolledTo.current === highlight) return;

    scrolledTo.current = highlight;
    // Unconditionally: working out whether this particular message is one of
    // the folded ones is more code than simply having them all present.
    setShowAll(true);
    /* And unfolded on a phone, for good: the ring that marks it fades after
       three seconds, and the message must not fold back up when it does. */
    setUnfolded((current) => new Set(current).add(highlight));

    const frame = requestAnimationFrame(() => {
      document
        .getElementById(messageAnchor(highlight))
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
    return () => cancelAnimationFrame(frame);
  }, [highlight, loadedMessages]);

  /*
   * Opening a ticket puts you at the newest message, not the oldest.
   *
   * A thread runs oldest-first, top to bottom, which is the right order to
   * read it in and the wrong place to arrive: what an agent opens a ticket for
   * is what was said last, and on anything longer than a screen that was at
   * the bottom of a scroll they had to make every single time. The fold above
   * already trims the history to three; this is the same argument applied to
   * where the pane starts.
   *
   * Aligned to the *top* of that message rather than scrolled to the bottom of
   * the pane, so a long reply opens at its first line. Landing on the last
   * paragraph of a wall of text is its own version of the same complaint.
   *
   * Once per ticket, and never over a link: `#message-…` says exactly which
   * message somebody meant, and the effect below owns the scroll in that case.
   * The pane is scrolled directly instead of through `scrollIntoView`, which
   * walks every scrollable ancestor -- here that includes the queue beside it.
   */
  const pane = useRef<HTMLDivElement>(null);
  const openedAt = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (openedAt.current === ticketId) return;
    if (!loadedMessages || loadedMessages.length === 0) return;
    if (/^#message-\d+$/.test(location.hash)) return;

    const newest = loadedMessages.at(-1);
    if (!newest) return;
    openedAt.current = ticketId;

    /* After paint: the bubbles are being rendered in this same commit, so
       measuring now would measure a pane that is still empty. */
    const frame = requestAnimationFrame(() => {
      const box = pane.current;
      const node = document.getElementById(messageAnchor(newest.id));
      if (!box || !node) return;
      const offset =
        node.getBoundingClientRect().top - box.getBoundingClientRect().top;
      /* Less a little, so the message reads as sitting in the pane rather
         than jammed against its top edge. */
      box.scrollTop += offset - 8;
    });
    return () => cancelAnimationFrame(frame);
  }, [ticketId, loadedMessages, location.hash]);

  /* The ring is a "here it is", not a state. Left on, it becomes another
     colour in a thread whose colours already mean something. */
  useEffect(() => {
    if (highlight === null) return;
    const timer = window.setTimeout(() => setHighlight(null), 3_000);
    return () => window.clearTimeout(timer);
  }, [highlight]);


  if (detail.isLoading) {
    return <p className="p-6 text-sm text-muted">Loading conversation…</p>;
  }

  if (detail.isError || !detail.data) {
    return <p className="p-6 text-sm text-red-600">Could not load this ticket.</p>;
  }

  const { ticket, channel, messages, activity, hasEmailThread } = detail.data;

  /*
   * `collapsible` is how many *could* be folded away, which stays true while
   * they are expanded -- `hidden` goes to nought the moment they are, and using
   * it for both is why the thread could be opened and never closed again.
   *
   * Counted over the customer-facing messages, not every message: a handful
   * of notes used to fold the whole email chain away. See `foldedCount`.
   */
  const collapsible = foldedCount(
    messages,
    isDesktop ? VISIBLE_MESSAGES : PHONE_VISIBLE_MESSAGES,
  );
  const hidden = showAll ? 0 : collapsible;
  const shown = hidden > 0 ? messages.slice(hidden) : messages;

  /*
   * The messages and the state changes, in one sequence.
   *
   * Interleaved rather than filed in a separate tab because the two only mean
   * anything together: "closed" straight after a reply is a resolution, and the
   * same line after a customer's third chase is not. Reading them apart loses
   * the bit that matters.
   *
   * Counting stays a question about *messages* -- the fold still says "3 earlier
   * messages", because that is what somebody is deciding whether to read. The
   * events belonging to the folded stretch go with it, which is why the cutoff
   * is the oldest visible message's time rather than a count of its own.
   */
  const oldestShown = shown.at(0);
  const cutoff =
    hidden > 0 && oldestShown
      ? new Date(oldestShown.sentAt).getTime()
      : -Infinity;

  const thread: ThreadItem[] = [
    ...shown.map((message) => ({
      kind: "message" as const,
      at: new Date(message.sentAt).getTime(),
      message,
    })),
    ...activity
      .filter(isThreadActivity)
      .map((entry) => ({
        kind: "activity" as const,
        at: new Date(entry.createdAt).getTime(),
        entry,
      }))
      .filter((item) => item.at >= cutoff),
  ].sort(
    (a, b) =>
      a.at - b.at ||
      /* Same second: the message first. A reply and the "closed" it triggered
         land together, and the change reads as a consequence of the message
         rather than as something that preceded it. */
      (a.kind === b.kind ? 0 : a.kind === "message" ? -1 : 1),
  );

  // Everyone on the ticket except the current agent -- showing yourself in the
  // "also here" list is noise.
  const others = presence.filter((entry) => entry.userId !== currentUserId);

  const header = (
    <header className="border-b border-line px-4 py-3 md:px-5 md:py-2">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          {/* The heading is the control: renaming happens here, in place. */}
          <h1 className="min-w-0">
            <TicketSubject
              ticketId={ticket.id}
              subject={ticket.subject}
              onSaved={invalidate}
            />
          </h1>
          <p className="mt-0.5 truncate text-xs text-muted">
            #{ticket.id} ·{" "}
            {/* The name opens their history. Whoever is about to reply is the
                person who most wants to know what was said last time. */}
            <ActivityLink
              kind="contact"
              id={ticket.contactId}
              name={ticket.contactName ?? ticket.contactEmail}
            >
              {ticket.contactName || ticket.contactEmail || "Unknown"}
            </ActivityLink>
            {ticket.contactName && ticket.contactEmail
              ? ` <${ticket.contactEmail}>`
              : ""}{" "}
            · via {channel.address}
            {ticket.companyName ? (
              <>
                {" · "}
                <ActivityLink
                  kind="company"
                  id={ticket.companyId}
                  name={ticket.companyName}
                />
              </>
            ) : null}
          </p>

          {/*
            Only when there is somebody beyond the contact. On a plain
            two-party ticket this row would be a permanent line repeating the
            address printed directly above it.
          */}
          {people.length > 1 ? <ThreadPeople people={people} /> : null}
        </div>

        {/*
          Last activity moved up here from a line of its own beneath the
          header. It is one short fact and it was costing a whole row above
          the conversation, on a screen whose complaint is that the reply box
          is too small -- so it went where there was already empty space.
        */}
        <div className="flex shrink-0 items-center gap-2">
          <span className="hidden text-[11px] text-muted lg:inline">
            Last activity {absoluteTime(ticket.lastMessageAt)}
          </span>
          <WatchButton ticketId={ticket.id} />
          <PresenceBar entries={others} />
        </div>
      </div>
    </header>
  );

  /*
   * The newest message, which is the one a phone opens unfolded -- what an
   * agent opens a ticket for is what was said last.
   */
  const newestId = messages.at(-1)?.id ?? null;

  /*
   * And the newest thing said to or by the customer, which a phone also opens
   * when it is not the same message. A note left after the last email used to
   * be "the newest", and folded the email it was written about down to a line
   * -- the same notes-hide-the-conversation fault as the fold above, at the
   * scale of one message.
   */
  const newestSaidId =
    messages.findLast((message) => message.kind !== "note")?.id ?? null;

  /* Shown from the tap, not from the refetch -- see `assign`. */
  const assigneeId = assign.isPending
    ? (assign.variables ?? null)
    : ticket.assigneeId;
  const assigneeName =
    assigneeId === null
      ? null
      : assigneeId === ticket.assigneeId
        ? ticket.assigneeName
        : (users.data?.find((user) => user.id === assigneeId)?.name ?? null);
  const status = changeStatus.isPending
    ? (changeStatus.variables ?? ticket.status)
    : ticket.status;

  return (
    <div className="flex h-full flex-col">
      {/*
        Above the thread on a desktop, where it is one short row. On a phone
        it is the first thing *in* the thread instead, and scrolls away with
        it as Gmail's subject does: pinned, a two-line subject and its meta
        line were a fifth of the screen spent restating a ticket the agent had
        already chosen to open. The app bar above it, with the way back, stays.
      */}
      {isDesktop ? header : null}

      {/* `ticket-thread` scopes the phone's reading styles in index.css to
          this pane, away from the other places that draw an email body. */}
      <div
        ref={pane}
        className="ticket-thread min-h-0 flex-1 overflow-y-auto md:px-5 md:py-4"
      >
        {isDesktop ? null : header}

        {/* `thread`, not `messages`: a ticket raised by hand and reassigned
            before anybody wrote on it has a history worth showing, and the
            empty state would otherwise hide it. */}
        {thread.length === 0 ? (
          <p className="p-4 text-sm text-muted md:p-0">
            No messages on this ticket yet.
          </p>
        ) : (
          /* No gaps on a phone: the messages run edge to edge, ruled off from
             one another, and space between them would be grey bands. */
          <ol className="md:space-y-4">
            {/*
              Long threads open on the recent end. A ticket that has been round
              three people is mostly history an agent has already read, and
              scrolling past it to find what was said last is the wrong way up.
              The older messages are one click away, not gone.
            */}
            {collapsible > 0 ? (
              <li className="border-b border-line p-2 md:border-0 md:p-0">
                <button
                  type="button"
                  onClick={() => setShowAll((value) => !value)}
                  aria-expanded={showAll}
                  className="w-full rounded-lg border border-dashed border-line py-2 text-xs text-muted hover:bg-surface-2 hover:text-brand-600 max-md:py-3 max-md:text-sm dark:hover:text-brand-400"
                >
                  {showAll
                    ? `Hide ${collapsible} earlier ${collapsible === 1 ? "message" : "messages"}`
                    : `Show ${collapsible} earlier ${collapsible === 1 ? "message" : "messages"}`}
                </button>
              </li>
            ) : null}

            {thread.map((item) =>
              item.kind === "message" ? (
                <li key={`m${item.message.id}`}>
                  <MessageBubble
                    message={item.message}
                    ticketId={ticketId}
                    currentUserId={currentUserId}
                    highlighted={item.message.id === highlight}
                    onAct={act}
                    onCopyLink={copyLink}
                    /*
                     * Phone only. The newest stays open, as does the newest
                     * email or reply when a note came after it (see
                     * `newestSaidId`), and so does a reply that failed to
                     * send -- that one is a customer still waiting, and a
                     * folded row is where it would be missed.
                     */
                    {...(isDesktop
                      ? {}
                      : {
                          collapsed:
                            item.message.id !== newestId &&
                            item.message.id !== newestSaidId &&
                            item.message.delivery !== "failed" &&
                            !unfolded.has(item.message.id),
                          onToggleCollapsed:
                            item.message.id === newestId ||
                            item.message.id === newestSaidId
                              ? undefined
                              : () => toggleFolded(item.message.id),
                        })}
                  />
                </li>
              ) : (
                /* Keyed apart from messages: the two id sequences are
                   independent and would otherwise collide. */
                <li
                  key={`a${item.entry.id}`}
                  className="px-4 py-1.5 md:p-0"
                >
                  <ActivityEntry entry={item.entry} />
                </li>
              ),
            )}
          </ol>
        )}
      </div>

      {/*
        The phone's action bar, in place of the reply box until it is asked
        for: who has the ticket, closing it, and replying, under the thumb
        rather than behind the Details sheet. Reassigning in particular was
        two screens deep, through a native select of bare names.
      */}
      {isDesktop || composerOpen ? null : (
        <div className="flex shrink-0 items-center gap-2 border-t border-line bg-surface px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          {/*
            Initials, not a labelled pill. Who has it is read at a glance from
            a face the desk knows, and a pill that said "Assigned to" and the
            whole name took half the bar -- which is now the SLA's, the other
            thing somebody opens a ticket on a phone to check.
          */}
          <button
            type="button"
            onClick={() => setAssigning(true)}
            aria-label={`Assigned to ${assigneeId === null ? "nobody" : (assigneeName ?? "somebody")} — change`}
            title={`Assigned to ${assigneeId === null ? "nobody" : (assigneeName ?? "somebody")}`}
            className="flex size-11 shrink-0 items-center justify-center rounded-full active:bg-surface-2"
          >
            {assigneeId === null ? (
              <span className="flex size-8 items-center justify-center rounded-full border border-dashed border-line text-[10px] font-medium text-faint">
                —
              </span>
            ) : (
              <Avatar
                size="md"
                agent={assigneeOf(users.data ?? [], assigneeId, assigneeName)}
              />
            )}
          </button>

          <div className="min-w-0 flex-1">{sla}</div>

          <button
            type="button"
            onClick={() =>
              changeStatus.mutate(status === "closed" ? "open" : "closed")
            }
            disabled={changeStatus.isPending}
            className="h-11 shrink-0 rounded-full border border-line px-4 text-sm text-content active:bg-surface-2 disabled:opacity-50"
          >
            {status === "closed" ? "Reopen" : "Close"}
          </button>

          <button
            type="button"
            onClick={() => setComposerOpen(true)}
            className="h-11 shrink-0 rounded-full bg-brand-600 px-5 text-sm font-medium text-white active:bg-brand-700"
          >
            {/* A draft saved from earlier is said out loud, so nobody starts
                a second reply not knowing the first is waiting. */}
            {presenceState === "replying" ? "Draft" : "Reply"}
          </button>
        </div>
      )}

      {assigning ? (
        <AssigneeSheet
          users={users.data ?? []}
          currentUserId={currentUserId}
          assigneeId={assigneeId}
          subject={ticket.subject}
          onPick={(id) => assign.mutate(id)}
          onClose={() => setAssigning(false)}
        />
      ) : null}

      <div className={cn(!isDesktop && !composerOpen && "hidden")}>
        {isDesktop ? null : (
          <div className="flex items-center border-t border-line bg-surface-2 px-2">
            <button
              type="button"
              onClick={() => setComposerOpen(false)}
              className="flex h-10 items-center gap-1.5 px-2 text-sm text-muted active:text-content"
            >
              <span aria-hidden>▾</span> Back to the conversation
            </button>
          </div>
        )}
        <Composer
          ticketChannelId={channel.id}
          replyChannelId={channel.replyChannelId}
          ticketChannel={channel.type}
          hasEmailThread={hasEmailThread}
          ticketId={ticketId}
          ticketSubject={ticket.subject}
          ticketContact={ticket.contactName ?? ticket.contactEmail}
          ticketContactEmail={ticket.contactEmail}
          /* The same list the participants line above draws, so the composer's
             "not on this reply" cannot disagree with the header's "on this
             thread". */
          people={people}
          /*
           * The message an ordinary reply answers, so the composer can open with
           * everybody who was on it already copied.
           *
           * Newest *inbound*, which is the same message the server addresses the
           * reply to -- see the Gmail send service's `resolveRecipients`. Picking
           * the newest message of any kind would hand the composer our own last
           * reply, whose Cc line is whatever we chose last time rather than who
           * the customer is talking to.
           */
          replyTo={messages.findLast((message) => message.kind === "inbound") ?? null}
          intent={intent}
          onIntentHandled={() => setIntent(null)}
          onDraftChange={(hasDraft) =>
            setPresenceState(hasDraft ? "replying" : "viewing")
          }
          onSent={() => {
            invalidate();
            /* Sent is done: back to reading, where the reply just appeared. */
            setComposerOpen(false);
          }}
        />
      </div>
    </div>
  );
}

/**
 * Everybody on the ticket, on one line, with the whole list a click away.
 *
 * Collapsed by default and truncated to a single line, because this sits in a
 * header that had a row taken out of it for being too tall -- and on a thread
 * with nine people on it the full list is three lines of addresses above every
 * conversation. Expanded, each address is its own copy button: the reason to
 * open this is nearly always to paste somebody into something else.
 */
function ThreadPeople({ people }: { people: Participant[] }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-0.5 flex min-w-0 items-baseline gap-1.5 text-xs text-muted">
      <span className="shrink-0">On this ticket</span>

      {open ? (
        /*
          Plain text, and deliberately not the copy buttons the side panel
          gives these same people.

          Those clamp: a `CopyButton` carries `max-width: 100%`, and inside a
          shrink-to-fit chip that percentage has nothing definite to resolve
          against -- Chrome settles the circle by cutting the first address
          eight pixels short of itself, which puts an ellipsis in the middle of
          an email address in the one control whose whole job is showing the
          whole thing. Text wraps instead of truncating, which is what "see all
          of them" asks for; copying stays in the panel, where each address has
          a row and a definite width to sit in.
        */
        <span className="min-w-0 break-words">
          {people.map((person, index) => (
            <span key={person.address.email}>
              {index > 0 ? ", " : ""}
              {describeParticipant(person)}
              {/*
                Said out loud rather than left to be inferred from the order.
                "Cc" is the fact an agent is looking for here: the difference
                between somebody in the conversation and somebody being kept
                informed of it.
              */}
              {person.copied ? <span className="text-faint"> (Cc)</span> : null}
            </span>
          ))}
        </span>
      ) : (
        /* Names when we have them, on the collapsed line: it is scanned for
           "is my account manager on this", which is a question about people
           rather than about addresses. The addresses are what expanding is
           for. */
        <span className="min-w-0 truncate">
          {people
            .map((person) => person.address.name ?? person.address.email)
            .join(", ")}
        </span>
      )}

      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className={cn(
          "shrink-0 rounded px-1 text-[11px] text-muted",
          "hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
        )}
      >
        {open ? "Fewer" : `All ${people.length}`}
      </button>
    </div>
  );
}
